// Catalog bulk tool: set or remove inventory.limited_stock on selected Shopify products.
import React, { useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import moment from 'moment'
import { ajaxPerso, meta } from '../../fnc'
import Loading from './Loading'

const refConst = require("../../constants.js").v

const META_NAMESPACE = "inventory"
const META_KEY = "limited_stock"
const DEFAULT_QTY = 8

const initialState = {
	items: [],
	submited: false,
	_isMounted: false,
	selectedStore: refConst.id_store_eio,
	actionMode: "set",
	displayQty: DEFAULT_QTY,
}

// Shopify may return metafield values as a JSON string or already-parsed object.
const parseLimitedStockMeta = (rawValue) => {
	if(!rawValue)	return null

	try {
		return typeof rawValue === "string"? JSON.parse(rawValue) : rawValue
	} catch (e) {
		return null
	}
}

// Estimate remaining display qty from inventory sold since the baseline snapshot.
const getRemaining = (metaData, inventoryQty) => {
	if(!metaData?.enabled)	return null

	let displayQty = parseInt(metaData.display_qty, 10)
	let baselineInventory = parseInt(metaData.baseline_inventory, 10)
	if(!displayQty || isNaN(baselineInventory))	return null

	let remaining = displayQty - (baselineInventory - (parseInt(inventoryQty, 10) || 0))
	return remaining > 0? remaining : 0
}

// Wrap meta.fetch in a Promise for bulk loading.
const fetchLimitedStockMeta = (shopId, productId) => {
	return new Promise((resolve) => {
		meta.fetch({
			shop: shopId,
			urlPrefix: `products/${productId}/`,
			namespace: META_NAMESPACE,
			key: META_KEY,
		}, (r) => {
			if(r.success && r.res?.length > 0)
				resolve(r.res[0])
			else
				resolve(null)
		})
	})
}

// Collapse variant rows from getItems into one product row with summed inventory.
const groupProductsByShopifyId = (rows) => {
	let byProduct = {}

	;(rows || []).forEach(row => {
		let productId = row.id_product_shopify
		if(!productId)	return

		if(!byProduct[productId]){
			byProduct[productId] = {
				id_product: row.id_product,
				id_product_shopify: productId,
				sku: row.sku,
				vendor: row.vendor,
				title: row.title,
				handle: row.handle,
				inventory_quantity: 0,
				limitedStockMeta: null,
				limitedStockData: null,
				status: null,
			}
		}

		byProduct[productId].inventory_quantity += parseInt(row.inventory_quantity, 10) || 0

		// Prefer a non-empty SKU if later variant rows have one.
		if((!byProduct[productId].sku || byProduct[productId].sku.length === 0) && row.sku)
			byProduct[productId].sku = row.sku
	})

	return Object.values(byProduct)
}

export default function BulkLimitedStock(props){
	const [stores, setStores] = useState([])
	const [items, setItems] = useState(initialState.items.slice(0))
	const [selectedStore, setSelectedStore] = useState(initialState.selectedStore)
	const [actionMode, setActionMode] = useState(initialState.actionMode)
	const [displayQty, setDisplayQty] = useState(initialState.displayQty)
	const [submited, setSubmited] = useState(initialState.submited)
	const [loadingMeta, setLoadingMeta] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [resultSummary, setResultSummary] = useState(null)
	const isMounted = useRef(false)

	useEffect(() => {
		isMounted.current = true
		return () => { isMounted.current = false }
	}, [])

	useEffect(() => {
		if(modalOpen)
			modalTrigger(true)
	}, [items, submited, selectedStore, stores, modalOpen, actionMode, displayQty, loadingMeta, resultSummary])

	useEffect(() => {
		if(!modalOpen)	return

		setSubmited(false)
		setResultSummary(null)
		loadProductsAndMetas()
	}, [selectedStore, modalOpen])

	// Reset local state when closing the modal.
	const resetPage = () => {
		setItems(initialState.items.slice(0))
		setSelectedStore(initialState.selectedStore)
		setActionMode(initialState.actionMode)
		setDisplayQty(initialState.displayQty)
		setSubmited(initialState.submited)
		setLoadingMeta(false)
		setResultSummary(null)
	}

	const triggerClick = () => {
		if(!props.itemsSelected || props.itemsSelected.length === 0){
			props.info({error: "Please select at least one catalog item first."})
			return
		}

		setStores((props.stores || []).filter(o => o.id_marketplace == refConst.id_marketplace_shopify))
		setModalOpen(true)
		modalTrigger(false)
	}

	// Load Shopify products for the catalog selection, then fetch limited_stock metas for stats.
	const loadProductsAndMetas = () => {
		if(!selectedStore || !props.itemsSelected?.length){
			setItems([])
			return
		}

		props.loading(true)
		setLoadingMeta(true)

		ajaxPerso({
			"api": "shopify",
			"trigger": "getItems",
			"shop": selectedStore,
			"idProductListJson": JSON.stringify(props.itemsSelected),
		}, (r) => {
			if(!isMounted.current)	return

			if(!r.success){
				props.loading(false)
				setLoadingMeta(false)
				props.info({error: r.error || "Could not load Shopify items for this selection."})
				setItems([])
				return
			}

			let grouped = groupProductsByShopifyId(r.data || [])

			Promise.all(grouped.map(item => {
				return fetchLimitedStockMeta(selectedStore, item.id_product_shopify).then(foundMeta => {
					item.limitedStockMeta = foundMeta
					item.limitedStockData = foundMeta? parseLimitedStockMeta(foundMeta.value) : null
					return item
				})
			})).then(enriched => {
				if(!isMounted.current)	return

				props.loading(false)
				setLoadingMeta(false)
				setItems(enriched)
			})
		})
	}

	// Build before/after style stats from the current item list.
	const buildStats = (list) => {
		let withLimited = 0
		let withoutLimited = 0
		let remainingSum = 0
		let remainingCount = 0
		let zeroOrGone = 0
		let foundCatalogIds = {}

		;(list || []).forEach(item => {
			if(item.id_product)
				foundCatalogIds[item.id_product] = true

			let data = item.limitedStockData
			if(data?.enabled){
				withLimited++
				let remaining = getRemaining(data, item.inventory_quantity)
				if(remaining !== null){
					remainingSum += remaining
					remainingCount++
					if(remaining <= 0)	zeroOrGone++
				}
			}else{
				withoutLimited++
			}
		})

		let selectedCatalog = props.itemsSelected?.length || 0
		let foundCount = Object.keys(foundCatalogIds).length

		return {
			selectedCatalog,
			onStore: (list || []).length,
			missingOnStore: Math.max(0, selectedCatalog - foundCount),
			withLimited,
			withoutLimited,
			avgRemaining: remainingCount > 0? Math.round((remainingSum / remainingCount) * 10) / 10 : null,
			zeroOrGone,
		}
	}

	// Create/update limited_stock on every loaded product.
	const applySetLimitedStock = async () => {
		let qty = parseInt(displayQty, 10)
		if(!qty || qty < 1){
			props.info({error: "Please enter a quantity greater than 0."})
			return
		}

		setSubmited(true)
		props.loading(true)

		let success = 0
		let error = 0
		let nextItems = []

		for (const item of items) {
			let metaValue = {
				display_qty: qty,
				baseline_inventory: parseInt(item.inventory_quantity, 10) || 0,
				enabled: true,
				set_at: parseInt(moment().format("X"), 10)
			}

			let result = await new Promise((resolve) => {
				meta.create({
					urlPrefix: `/products/${item.id_product_shopify}/`,
					namespace: META_NAMESPACE,
					key: META_KEY,
					shop: selectedStore,
					value: metaValue,
				}, resolve)
			})

			if(result.success){
				success++
				nextItems.push(Object.assign({}, item, {
					limitedStockMeta: {id: result.json?.id || item.limitedStockMeta?.id, value: JSON.stringify(metaValue)},
					limitedStockData: metaValue,
					status: "set",
				}))
			}else{
				error++
				nextItems.push(Object.assign({}, item, {
					status: "error",
					statusMessage: result.error || "Save failed",
				}))
			}
		}

		if(!isMounted.current)	return

		setItems(nextItems)
		setResultSummary({action: "set", success, error, qty})
		setSubmited(false)
		props.loading(false)

		props.info({
			success: success > 0? `Limited stock set on ${success} product(s) (display qty ${qty}).` : null,
			error: error > 0? `Failed on ${error} product(s).` : null,
		})
	}

	// Delete limited_stock where it currently exists.
	const applyRemoveLimitedStock = async () => {
		setSubmited(true)
		props.loading(true)

		let success = 0
		let error = 0
		let skipped = 0
		let nextItems = []

		for (const item of items) {
			if(!item.limitedStockMeta?.id){
				skipped++
				nextItems.push(Object.assign({}, item, {status: "skipped"}))
				continue
			}

			let result = await new Promise((resolve) => {
				meta.delete({
					shop: selectedStore,
					id: item.limitedStockMeta.id,
				}, resolve)
			})

			if(result.success){
				success++
				nextItems.push(Object.assign({}, item, {
					limitedStockMeta: null,
					limitedStockData: null,
					status: "removed",
				}))
			}else{
				error++
				nextItems.push(Object.assign({}, item, {
					status: "error",
					statusMessage: result.error || "Delete failed",
				}))
			}
		}

		if(!isMounted.current)	return

		setItems(nextItems)
		setResultSummary({action: "remove", success, error, skipped})
		setSubmited(false)
		props.loading(false)

		props.info({
			success: success > 0? `Limited stock removed from ${success} product(s).` : (skipped > 0 && error === 0? `Nothing to remove (${skipped} already without limited stock).` : null),
			error: error > 0? `Failed on ${error} product(s).` : null,
		})
	}

	const modalTrigger = (refresh) => {
		let storeSelectedObj = stores.length > 0 && selectedStore? stores.find(o => o.id == selectedStore) : null
		let stats = buildStats(items)

		props.modal({
			show: refresh? false : true,
			key: "bulk_limited_stock_modal",
			title: "Bulk Limited Stock",
			options: {
				width: "modal-xl"
			},
			html: () => {
				return stores.length === 0? <Loading loading={true} centered={true}/> : <div>
					<div className="container bg-light p-3 border mb-3">
						<div className="row">
							<div className="col-sm">
								<label className="small text-muted mb-1">Shopify store</label>
								<Select
									styles={refConst.styleList}
									onChange={(selectedOption) => {
										setSelectedStore(selectedOption && selectedOption.value != '-'? selectedOption.value : initialState.selectedStore)
									}}
									options={stores.map(o => ({value: o.id, label: o.name}))}
									value={selectedStore && storeSelectedObj? {
										value: selectedStore,
										label: storeSelectedObj.name,
									} : null}
									isSearchable={true}
									isMulti={false}
									placeholder={"Select the store"}
								/>
							</div>
							<div className="col-sm">
								<label className="small text-muted mb-1">Action</label>
								<Select
									styles={refConst.styleList}
									onChange={(selectedOption) => {
										setActionMode(selectedOption?.value || initialState.actionMode)
									}}
									options={[
										{value: "set", label: "Set / update limited stock"},
										{value: "remove", label: "Remove limited stock"},
									]}
									value={{
										value: actionMode,
										label: actionMode === "remove"? "Remove limited stock" : "Set / update limited stock",
									}}
									isSearchable={false}
									isMulti={false}
								/>
							</div>
							{
								actionMode === "set"?
									<div className="col-sm">
										<label className="small text-muted mb-1" htmlFor="bulkLimitedStockQty">Display qty (X in &quot;Only X left&quot;)</label>
										<input
											id="bulkLimitedStockQty"
											type="number"
											min="1"
											className="form-control"
											value={displayQty}
											disabled={submited}
											onChange={(e) => setDisplayQty(e.target.value)}
										/>
										<small className="form-text text-muted">Default is {DEFAULT_QTY}.</small>
									</div>
								: null
							}
						</div>
					</div>

					{
						loadingMeta?
							<div className="alert alert-secondary">Loading limited stock status for selected products...</div>
						:
							<div className="row mb-3">
								<div className="col-md-3 mb-2">
									<div className="border rounded p-2 bg-white h-100">
										<div className="small text-muted">Selected / on store</div>
										<div className="h5 mb-0">{stats.selectedCatalog} / {stats.onStore}</div>
										{stats.missingOnStore > 0? <div className="small text-warning">{stats.missingOnStore} missing on this store</div> : null}
									</div>
								</div>
								<div className="col-md-3 mb-2">
									<div className="border rounded p-2 bg-white h-100">
										<div className="small text-muted">Already have limited stock</div>
										<div className="h5 mb-0 text-success">{stats.withLimited}</div>
									</div>
								</div>
								<div className="col-md-3 mb-2">
									<div className="border rounded p-2 bg-white h-100">
										<div className="small text-muted">Without limited stock</div>
										<div className="h5 mb-0 text-warning">{stats.withoutLimited}</div>
									</div>
								</div>
								<div className="col-md-3 mb-2">
									<div className="border rounded p-2 bg-white h-100">
										<div className="small text-muted">Avg remaining (active)</div>
										<div className="h5 mb-0">{stats.avgRemaining !== null? stats.avgRemaining : "—"}</div>
										{stats.zeroOrGone > 0? <div className="small text-danger">{stats.zeroOrGone} at 0 left</div> : null}
									</div>
								</div>
							</div>
					}

					{
						resultSummary?
							<div className={"alert " + (resultSummary.error > 0? "alert-warning" : "alert-success")}>
								{
									resultSummary.action === "set"?
										<span>Done: set limited stock on <strong>{resultSummary.success}</strong> product(s) with display qty <strong>{resultSummary.qty}</strong>{resultSummary.error > 0? <> · <strong>{resultSummary.error}</strong> failed</> : null}.</span>
									:
										<span>Done: removed from <strong>{resultSummary.success}</strong>{resultSummary.skipped > 0? <> · skipped <strong>{resultSummary.skipped}</strong> (already none)</> : null}{resultSummary.error > 0? <> · <strong>{resultSummary.error}</strong> failed</> : null}.</span>
								}
							</div>
						: null
					}

					<table className="table table-sm">
						<thead>
							<tr>
								<th className="bg-info text-white">SKU</th>
								<th className="bg-info text-white">Vendor</th>
								<th className="bg-info text-white">Inventory</th>
								<th className="bg-info text-white">Limited stock</th>
								<th className="bg-info text-white">Remaining</th>
								{resultSummary? <th className="bg-primary text-white">Result</th> : null}
							</tr>
						</thead>
						<tbody>
							{
								items.length > 0?
									items.map(item => {
										let remaining = getRemaining(item.limitedStockData, item.inventory_quantity)
										let hasIt = !!item.limitedStockData?.enabled

										return <tr key={item.id_product_shopify + "_bulkLimitedStock"}>
											<td>
												{item.sku || "—"}
												{storeSelectedObj?.account_name?
													<div className="small">
														<a href={"https://" + storeSelectedObj.account_name + "/admin/products/" + item.id_product_shopify} target="_blank" rel="noopener noreferrer">Admin</a>
													</div>
												: null}
											</td>
											<td>{item.vendor || "—"}</td>
											<td>{item.inventory_quantity}</td>
											<td>
												{
													hasIt?
														<span className="badge badge-success">Yes · {item.limitedStockData.display_qty}</span>
													:
														<span className="badge badge-secondary">No</span>
												}
											</td>
											<td>{hasIt && remaining !== null? remaining : "—"}</td>
											{
												resultSummary?
													<td>
														{
															item.status === "set"? <span className="text-success">Set</span>
															: item.status === "removed"? <span className="text-success">Removed</span>
															: item.status === "skipped"? <span className="text-muted">Skipped</span>
															: item.status === "error"? <span className="text-danger">{item.statusMessage || "Error"}</span>
															: "—"
														}
													</td>
												: null
											}
										</tr>
									})
								:
									<tr><td colSpan={resultSummary? 6 : 5} className="text-center text-muted">
										{loadingMeta? "Loading..." : "No Shopify products found for this selection on the selected store."}
									</td></tr>
							}
						</tbody>
					</table>
				</div>
			},
			ok: !submited && !loadingMeta && items.length > 0? {
				title: actionMode === "remove"? "Remove limited stock" : "Set limited stock",
				fnc: () => {
					if(actionMode === "remove")
						applyRemoveLimitedStock()
					else
						applySetLimitedStock()
				}
			} : false,
			return: {
				title: "Exit",
				fnc: (popup, close) => {
					setModalOpen(false)
					resetPage()
					close()
				}
			},
			exit: (popup, close) => {
				setModalOpen(false)
				resetPage()
				close()
			}
		})
	}

	return (
		<div className="list-group-item list-group-item-action p-2 text-center" onClick={triggerClick}>
			{props.title || "Limited Stock"}
		</div>
	)
}
